<template>
  <o-card>
    <template slot="header">
      <h5 class="mb-0">{{ $t('sandoz.plan_data') }}</h5>
    </template>

    <!-- Generic single-track (non-split) -->
    <div v-if="!hasGxOtcSplit">
      <plan-row :label="$t('sandoz.turnover')" v-model="item.turnover" :readonly="readonly"/>
      <plan-row :label="$t('sandoz.turnover_expectation')" v-model="item.turnover_expectation" :readonly="readonly"/>
      <plan-row :label="$t('sandoz.discount_percentage')"
                v-model="item.discount_percentage"
                :readonly="readonly" suffix="%"/>
      <plan-row :label="$t('sandoz.discount')" v-model="item.discount" :readonly="readonly"/>
      <plan-row :label="$t('sandoz.marketing_activities')" v-model="item.marketing_activities" :readonly="readonly"/>
      <plan-row :label="$t('sandoz.advertisement')" v-model="item.advertisement" :readonly="readonly"/>
      <plan-row :label="$t('sandoz.other_marketing')" v-model="item.other_marketing" :readonly="readonly"/>
    </div>

    <!-- GX/OTC split (KAM types) -->
    <div v-else>
      <b-row>
        <b-col md="6">
          <h6>{{ $t('sandoz.gx') }}</h6>
          <plan-row :label="$t('sandoz.turnover_gx')" v-model="item.turnover_gx" :readonly="readonly"/>
          <plan-row :label="$t('sandoz.turnover_gx_expectation')" v-model="item.turnover_gx_expectation" :readonly="readonly"/>
          <plan-row :label="$t('sandoz.discount_gx_percentage')" v-model="item.discount_gx_percentage" :readonly="readonly" suffix="%"/>
          <plan-row :label="$t('sandoz.discount_gx')" v-model="item.discount_gx" :readonly="readonly"/>
          <plan-row :label="$t('sandoz.marketing_activities_gx')" v-model="item.marketing_activities_gx" :readonly="readonly"/>
          <plan-row :label="$t('sandoz.advertisement_gx')" v-model="item.advertisement_gx" :readonly="readonly"/>
          <plan-row :label="$t('sandoz.other_marketing_gx')" v-model="item.other_marketing_gx" :readonly="readonly"/>
        </b-col>
        <b-col md="6">
          <h6>{{ $t('sandoz.otc') }}</h6>
          <plan-row :label="$t('sandoz.turnover_otc')" v-model="item.turnover_otc" :readonly="readonly"/>
          <plan-row :label="$t('sandoz.turnover_otc_expectation')" v-model="item.turnover_otc_expectation" :readonly="readonly"/>
          <plan-row :label="$t('sandoz.discount_otc_percentage')" v-model="item.discount_otc_percentage" :readonly="readonly" suffix="%"/>
          <plan-row :label="$t('sandoz.discount_otc')" v-model="item.discount_otc" :readonly="readonly"/>
          <plan-row :label="$t('sandoz.marketing_activities_otc')" v-model="item.marketing_activities_otc" :readonly="readonly"/>
          <plan-row :label="$t('sandoz.advertisement_otc')" v-model="item.advertisement_otc" :readonly="readonly"/>
          <plan-row :label="$t('sandoz.other_marketing_otc')" v-model="item.other_marketing_otc" :readonly="readonly"/>
        </b-col>
      </b-row>
    </div>

    <div v-if="features.has_kuko_conditions && derived && derived.has_kuko"
         class="mt-3 p-2 border rounded bg-light derived-from-kuko">
      <small class="text-muted d-block mb-1">{{ $t('sandoz.derived_from_kuko') }}</small>
      <div class="d-flex flex-wrap" style="gap: 1rem">
        <span>{{ $t('sandoz.kuko_products') }}: <strong>{{ derived.products_count }}</strong></span>
        <span>{{ $t('sandoz.kuko_product_groups') }}: <strong>{{ derived.product_groups_count }}</strong></span>
        <span>{{ $t('sandoz.kuko_special_products') }}: <strong>{{ derived.special_products_count }}</strong></span>
        <span>{{ $t('sandoz.discount_total_new_condition') }}: <strong>{{ formatNumber(derived.total_new_condition) }}</strong></span>
        <span>{{ $t('sandoz.discount_max_rebate') }}: <strong>{{ formatNumber(derived.max_rebate) }}%</strong></span>
      </div>
    </div>

    <p class="text-muted small mt-3">{{ $t('sandoz.plan_data_phase4_note') }}</p>
  </o-card>
</template>

<script>
import axios from 'axios'
import sectionMixin from '../sectionMixin'

/**
 * Plan data: turnover / expectation / discount / marketing activities, with a GX/OTC split
 * for KAM contract types. Auto-calculation of discount fields (the watchers in
 * kam/ContractDetail.vue) will be ported in a follow-up — for now the % and CHF fields
 * are both editable.
 *
 * On KUKO-enabled sales lines the section also pulls a read-only "derived from KUKO" summary
 * (counts + aggregate new-condition / max-rebate) from the backend so the manual discount
 * fields can be sanity-checked against the conditions the user just entered in SectionKuko.
 */
export default {
  name: 'SectionPlanData',
  mixins: [sectionMixin],
  components: {
    PlanRow: {
      props: ['label', 'value', 'readonly', 'suffix'],
      template: `
        <div class="form-group">
          <label>{{ label }}</label>
          <b-input-group :append="suffix">
            <b-form-input :value="value"
                          type="number"
                          step="0.01"
                          :readonly="readonly"
                          @input="$emit('input', $event)"/>
          </b-input-group>
        </div>
      `
    }
  },
  data() {
    return {
      derived: null
    }
  },
  computed: {
    hasGxOtcSplit() {
      const type = this.item.type || ''
      return type.startsWith('KAM_') || type.startsWith('BIOS_KAM_')
    }
  },
  created() {
    this.loadDerived()
  },
  watch: {
    'item.id'() {
      this.loadDerived()
    },
    'features.has_kuko_conditions'() {
      this.loadDerived()
    }
  },
  methods: {
    async loadDerived() {
      if (!this.item.id || !this.features.has_kuko_conditions) {
        this.derived = null
        return
      }
      try {
        const resp = await axios.get(`/api/sandoz/contracts/${this.item.id}/kuko-conditions/derived-discount`)
        this.derived = resp.data
      } catch (err) {
        // Subtotal is advisory — never break the page if it fails.
        this.derived = null
      }
    },
    formatNumber(value) {
      return Number(value || 0).toFixed(2)
    }
  }
}
</script>
